import { AlarmMedia, DataVersion } from '@hakimo-ui/hakimo/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { withDataVersion } from '@hakimo-ui/hakimo/util';
import useAuthenticatedRequest from '../shared/useAuthenticatedRequest';
dayjs.extend(utc);

interface Options {
  id: string;
  timestamp: number;
  debug?: boolean;
  boxes?: boolean;
  sharedToken?: string | null;
  isSharedLocationAlarm?: boolean;
  refetchInterval?: (data: AlarmMedia | undefined) => number | false;
  dataVersion?: DataVersion;
}

export function useAlarmMedia(options: Options) {
  const {
    id,
    timestamp,
    debug = false,
    sharedToken,
    isSharedLocationAlarm,
    refetchInterval,
    boxes,
    dataVersion,
  } = options;
  const date = dayjs.utc(timestamp);
  const partitionKey = date.format('YYYYMM');
  const url = isSharedLocationAlarm
    ? boxes
      ? `/shared/v2/location_alarm/media?sharedToken=${sharedToken}&id=${id}&includeBoxes=true&partitionKey=${partitionKey}`
      : `/shared/v2/location_alarm/media?sharedToken=${sharedToken}&id=${id}&partitionKey=${partitionKey}`
    : sharedToken
    ? boxes
      ? `/shared/v2/alarm/media?sharedToken=${sharedToken}&includeBoxes=true&partitionKey=${partitionKey}`
      : `/shared/v2/alarm/media?sharedToken=${sharedToken}&partitionKey=${partitionKey}`
    : debug
    ? `/v2/orm/alarm/debug-media/${id}?partitionKey=${partitionKey}`
    : boxes
    ? `/v2/orm/alarm/media/${id}?includeBoxes=true&partitionKey=${partitionKey}`
    : `/v2/orm/alarm/media/${id}?partitionKey=${partitionKey}`;
  const request = new Request(url);
  withDataVersion(request, dataVersion);

  return useAuthenticatedRequest<AlarmMedia>(request, {
    // `boxes` is in the key because it changes the URL (`includeBoxes=true`):
    // callers disagree on it (AlarmOverview always asks for boxes, AlarmMedia
    // only on v3), so leaving it out let them share a key and read each other's
    // payload.
    queryKey: ['alarms', 'media', debug, id, boxes, dataVersion],
    responseModifier: async (response) => {
      const respJson = await response.json();
      return respJson.payload;
    },
    refetchInterval,
    cacheTime: 0,
    noAuth: typeof sharedToken === 'string',
  });
}

export default useAlarmMedia;
